import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import {
  Loader2, MapPin, Car, Bike, AlertCircle, ShieldCheck, Wallet, Phone, Navigation, CheckCircle2,
} from "lucide-react";
import {
  getMyDriver, setDriverOnline, getDriverInbox, getDriverEarnings,
  driverAccept, driverReject, driverStart, driverComplete,
  formatFcfa, formatDuration, NDJAMENA_CENTER,
} from "../../lib/ride";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";

const STATUS_BADGE = {
  pending: { label: "En attente de validation", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Compte validé", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Refusé", cls: "bg-red-50 text-red-700 border-red-200" },
};

export default function DriverDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const refresh = useCallback(async () => {
    try {
      const me = await getMyDriver();
      setDriver(me);
      if (me.status === "approved") {
        const [e, ib] = await Promise.all([
          getDriverEarnings().catch(() => null),
          getDriverInbox().catch(() => ({ rides: [] })),
        ]);
        setEarnings(e);
        setInbox(ib.rides || []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Aucun profil chauffeur");
    }
  }, []);

  useEffect(() => {
    if (!user || user === false) {
      navigate("/login?next=/ride/driver");
      return;
    }
    refresh();
    const t = setInterval(refresh, 6000);
    return () => clearInterval(t);
  }, [refresh, user, navigate]);

  const toggleOnline = async (checked) => {
    setBusy("online");
    try {
      let lat = driver?.current_lat;
      let lng = driver?.current_lng;
      if (checked && !lat && navigator.geolocation) {
        await new Promise((resolve) =>
          navigator.geolocation.getCurrentPosition(
            (p) => { lat = p.coords.latitude; lng = p.coords.longitude; resolve(); },
            () => { lat = NDJAMENA_CENTER.lat; lng = NDJAMENA_CENTER.lng; resolve(); },
            { timeout: 4000 }
          )
        );
      }
      const upd = await setDriverOnline({ online: checked, lat, lng });
      setDriver(upd);
      toast.success(checked ? "Vous êtes en ligne" : "Vous êtes hors ligne");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy("");
    }
  };

  const doAccept = async (rideId) => {
    setBusy(rideId);
    try {
      await driverAccept(rideId);
      toast.success("Course acceptée — démarrez la navigation");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'accepter");
    } finally {
      setBusy("");
    }
  };
  const doReject = async (rideId) => { setBusy(rideId); try { await driverReject(rideId); toast.info("Course ignorée"); refresh(); } finally { setBusy(""); } };

  // Find current accepted/in_progress ride if any
  const [activeRide, setActiveRide] = useState(null);
  useEffect(() => {
    if (!driver || driver.status !== "approved") return;
    // Use inbox endpoint won't show our own accepted, fetch via /me/history is client-side only.
    // Instead, fetch driver's "current" via /api/ride/admin/rides filter? Easier: derive from inbox + a separate poll.
    // We'll do a lightweight call to /api/ride/{id} only when we accept, but simpler approach: query /ride/admin/rides
    // For now show "active" only after refresh of inbox (where it stops appearing). Add a dedicated endpoint later.
    setActiveRide(null);
  }, [driver]);

  const startNavigation = (lat, lng) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
    window.open(url, "_blank");
  };

  if (error) {
    return (
      <div className="max-w-md mx-auto px-5 py-12 text-center" data-testid="driver-dashboard-empty">
        <AlertCircle className="w-12 h-12 mx-auto text-brand-inkMuted mb-3" />
        <p className="text-brand-ink mb-3">{error}</p>
        <Link to="/ride/driver/signup">
          <Button className="rounded-full bg-brand-gold hover:bg-brand-goldDark text-brand-black">Devenir chauffeur</Button>
        </Link>
      </div>
    );
  }
  if (!driver) return <div className="text-center py-16 text-brand-inkSoft">Chargement…</div>;

  const badge = STATUS_BADGE[driver.status] || STATUS_BADGE.pending;
  const VehIcon = driver.vehicle_type === "moto" ? Bike : Car;
  const approved = driver.status === "approved";

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="driver-dashboard">
      <Link to="/ride" className="text-xs text-brand-inkSoft hover:text-brand-ink">← Retour</Link>

      <div className="flex items-start justify-between gap-3 flex-wrap mt-2 mb-5">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-ink">Espace chauffeur</h1>
          <div className="flex items-center gap-2 mt-1.5">
            <VehIcon className="w-4 h-4 text-brand-gold" />
            <span className="text-sm text-brand-inkSoft">
              {driver.vehicle_type === "moto" ? "Moto-taxi" : "Taxi"} · {driver.vehicle_plate}
            </span>
          </div>
        </div>
        <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${badge.cls}`} data-testid={`driver-status-${driver.status}`}>
          {driver.status === "approved" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
          {badge.label}
        </div>
      </div>

      {!approved && (
        <Card className="rounded-2xl border-brand-line bg-brand-sandlight p-4 mb-5">
          <p className="text-sm text-brand-inkSoft leading-relaxed">
            <strong className="text-brand-ink">En attente :</strong> votre dossier est en cours d'examen par l'équipe Kanama.
            Vous serez notifié(e) dès la validation. Vous pouvez modifier vos documents plus tard depuis cet écran.
          </p>
        </Card>
      )}

      {approved && (
        <>
          {/* Online toggle */}
          <Card className="rounded-2xl border-brand-line p-4 mb-4 flex items-center justify-between gap-4" data-testid="driver-online-toggle">
            <div>
              <div className="font-heading font-semibold text-brand-ink">Disponibilité</div>
              <div className="text-xs text-brand-inkSoft">
                {driver.online ? "Vous recevez les demandes de course" : "Activez pour recevoir des courses"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-semibold ${driver.online ? "text-emerald-600" : "text-brand-inkMuted"}`}>
                {driver.online ? "EN LIGNE" : "HORS LIGNE"}
              </span>
              <Switch checked={!!driver.online} onCheckedChange={toggleOnline} disabled={busy === "online"} data-testid="driver-online-switch" />
            </div>
          </Card>

          {/* Earnings */}
          {earnings && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <Card className="rounded-2xl border-brand-line p-4" data-testid="driver-earnings-today">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-brand-inkMuted font-bold mb-1.5">
                  <Wallet className="w-3 h-3" /> Aujourd'hui
                </div>
                <div className="font-heading text-2xl font-bold text-brand-ink">{formatFcfa(earnings.today.earnings_xaf)}</div>
                <div className="text-xs text-brand-inkSoft mt-0.5">{earnings.today.rides} course{earnings.today.rides > 1 ? "s" : ""}</div>
              </Card>
              <Card className="rounded-2xl border-brand-line p-4" data-testid="driver-earnings-lifetime">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-brand-inkMuted font-bold mb-1.5">
                  <Wallet className="w-3 h-3" /> Total
                </div>
                <div className="font-heading text-2xl font-bold text-brand-ink">{formatFcfa(earnings.lifetime.earnings_xaf)}</div>
                <div className="text-xs text-brand-inkSoft mt-0.5">{earnings.lifetime.rides} course{earnings.lifetime.rides > 1 ? "s" : ""} · note {driver.rating_avg.toFixed(1)} ★</div>
              </Card>
            </div>
          )}

          {/* Inbox */}
          <h2 className="font-heading text-lg font-semibold text-brand-ink mb-2 mt-6">Demandes de course</h2>
          {!driver.online && (
            <Card className="rounded-2xl border-brand-line bg-brand-sandlight p-4 text-sm text-brand-inkSoft" data-testid="driver-inbox-offline">
              Passez en ligne pour voir les demandes
            </Card>
          )}
          {driver.online && inbox.length === 0 && (
            <Card className="rounded-2xl border-brand-line p-6 text-center text-sm text-brand-inkSoft flex flex-col items-center gap-2" data-testid="driver-inbox-empty">
              <Loader2 className="w-5 h-5 animate-spin text-brand-gold" />
              En attente de nouvelles demandes…
            </Card>
          )}
          <div className="space-y-3">
            {inbox.map((r) => (
              <Card key={r.id} className="rounded-2xl border-brand-line p-4" data-testid={`driver-ride-${r.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 text-sm space-y-1">
                    <div className="flex gap-2"><span className="w-2 h-2 rounded-full bg-brand-gold mt-1.5 shrink-0" /><span className="text-brand-ink">{r.pickup.address || `${r.pickup.lat.toFixed(4)}, ${r.pickup.lng.toFixed(4)}`}</span></div>
                    <div className="flex gap-2"><span className="w-2 h-2 rounded-full bg-brand-terracotta mt-1.5 shrink-0" /><span className="text-brand-ink">{r.destination.address || `${r.destination.lat.toFixed(4)}, ${r.destination.lng.toFixed(4)}`}</span></div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-heading text-xl font-bold text-brand-ink">{formatFcfa(r.estimated_price)}</div>
                    <div className="text-[10px] uppercase tracking-wider text-brand-inkMuted">{r.distance_km} km · {formatDuration(r.duration_min)}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={() => doReject(r.id)} disabled={busy === r.id} variant="outline" className="rounded-full border-brand-line text-brand-inkSoft" data-testid={`driver-ride-reject-${r.id}`}>Ignorer</Button>
                  <Button onClick={() => doAccept(r.id)} disabled={busy === r.id} className="rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white font-semibold" data-testid={`driver-ride-accept-${r.id}`}>Accepter</Button>
                </div>
              </Card>
            ))}
          </div>

          <p className="text-[11px] text-brand-inkMuted mt-6 text-center">
            <MapPin className="w-3 h-3 inline -mt-0.5 mr-0.5" />
            Phase 1 · zone N'Djamena uniquement
          </p>
        </>
      )}
    </div>
  );
}
